/**
 * Voter demographics.
 * - Statewide ethnic / age figures: derived from Election Commission (SPR) electoral roll
 *   and DOSM data, plus ISEAS analysis of the 2022 Johor vote (Undi18 / age cohorts).
 * - Per-seat ethnic composition: ESTIMATES based on electoral-roll research and academic
 *   constituency profiles. Official ethnic breakdowns are not published at seat level, so
 *   these are clearly labelled as estimates throughout the UI.
 */

export interface EthnicMix {
  malay: number;
  chinese: number;
  indian: number;
  others: number;
}

export interface StateDemographics {
  source: string;
  note: string;
  ethnic: EthnicMix; // statewide electorate
  ageBands: { band: string; pct: number }[];
  gender: { male: number; female: number };
  newVoters: {
    label: string;
    value: string;
    note: string;
  };
  seatEthnic: Record<string, EthnicMix>; // by DUN code, estimates
}

// Per-DUN ethnic composition estimates (% of electorate). Rounded; for orientation only.
const seatEthnic: Record<string, EthnicMix> = {
  N01: { malay: 62, chinese: 22, indian: 15, others: 1 },
  N02: { malay: 47, chinese: 41, indian: 11, others: 1 },
  N03: { malay: 66, chinese: 23, indian: 10, others: 1 },
  N04: { malay: 58, chinese: 14, indian: 27, others: 1 },
  N05: { malay: 60, chinese: 27, indian: 12, others: 1 },
  N06: { malay: 40, chinese: 41, indian: 18, others: 1 },
  N07: { malay: 88, chinese: 8, indian: 3, others: 1 },
  N08: { malay: 79, chinese: 17, indian: 3, others: 1 },
  N09: { malay: 71, chinese: 20, indian: 8, others: 1 },
  N10: { malay: 45, chinese: 47, indian: 7, others: 1 },
  N11: { malay: 74, chinese: 18, indian: 7, others: 1 },
  N12: { malay: 22, chinese: 70, indian: 7, others: 1 },
  N13: { malay: 70, chinese: 23, indian: 6, others: 1 },
  N14: { malay: 72, chinese: 17, indian: 10, others: 1 },
  N15: { malay: 64, chinese: 31, indian: 4, others: 1 },
  N16: { malay: 80, chinese: 16, indian: 3, others: 1 },
  N17: { malay: 78, chinese: 18, indian: 3, others: 1 },
  N18: { malay: 86, chinese: 11, indian: 2, others: 1 },
  N19: { malay: 39, chinese: 54, indian: 6, others: 1 },
  N20: { malay: 84, chinese: 12, indian: 3, others: 1 },
  N21: { malay: 76, chinese: 14, indian: 9, others: 1 },
  N22: { malay: 80, chinese: 13, indian: 6, others: 1 },
  N23: { malay: 40, chinese: 52, indian: 7, others: 1 },
  N24: { malay: 73, chinese: 21, indian: 5, others: 1 },
  N25: { malay: 72, chinese: 20, indian: 7, others: 1 },
  N26: { malay: 66, chinese: 22, indian: 11, others: 1 },
  N27: { malay: 63, chinese: 22, indian: 14, others: 1 },
  N28: { malay: 30, chinese: 58, indian: 11, others: 1 },
  N29: { malay: 63, chinese: 26, indian: 10, others: 1 },
  N30: { malay: 33, chinese: 52, indian: 14, others: 1 },
  N31: { malay: 58, chinese: 17, indian: 24, others: 1 },
  N32: { malay: 78, chinese: 16, indian: 5, others: 1 },
  N33: { malay: 64, chinese: 17, indian: 18, others: 1 },
  N34: { malay: 80, chinese: 13, indian: 6, others: 1 },
  N35: { malay: 82, chinese: 11, indian: 6, others: 1 },
  N36: { malay: 80, chinese: 14, indian: 5, others: 1 },
  N37: { malay: 84, chinese: 12, indian: 3, others: 1 },
  N38: { malay: 85, chinese: 9, indian: 5, others: 1 },
  N39: { malay: 86, chinese: 11, indian: 2, others: 1 },
  N40: { malay: 62, chinese: 23, indian: 14, others: 1 },
  N41: { malay: 55, chinese: 34, indian: 10, others: 1 },
  N42: { malay: 40, chinese: 49, indian: 10, others: 1 },
  N43: { malay: 60, chinese: 30, indian: 9, others: 1 },
  N44: { malay: 62, chinese: 26, indian: 11, others: 1 },
  N45: { malay: 35, chinese: 58, indian: 6, others: 1 },
  N46: { malay: 33, chinese: 58, indian: 8, others: 1 },
  N47: { malay: 64, chinese: 24, indian: 11, others: 1 },
  N48: { malay: 28, chinese: 64, indian: 7, others: 1 },
  N49: { malay: 64, chinese: 27, indian: 8, others: 1 },
  N50: { malay: 60, chinese: 28, indian: 11, others: 1 },
  N51: { malay: 52, chinese: 33, indian: 14, others: 1 },
  N52: { malay: 33, chinese: 56, indian: 10, others: 1 },
  N53: { malay: 78, chinese: 16, indian: 5, others: 1 },
  N54: { malay: 70, chinese: 22, indian: 7, others: 1 },
  N55: { malay: 45, chinese: 47, indian: 7, others: 1 },
  N56: { malay: 64, chinese: 31, indian: 4, others: 1 },
};

export const johorDemographics: StateDemographics = {
  source:
    "Estimates compiled from the Election Commission of Malaysia (SPR) electoral roll, DOSM, and ISEAS analysis of the 2022 Johor vote.",
  note: "Per-seat ethnic figures are research estimates of the registered electorate, not official EC tabulations. Use as orientation only.",
  ethnic: { malay: 58, chinese: 33, indian: 8, others: 1 },
  ageBands: [
    { band: "18–20", pct: 6 },
    { band: "21–29", pct: 20 },
    { band: "30–39", pct: 22 },
    { band: "40–49", pct: 19 },
    { band: "50–59", pct: 15 },
    { band: "60+", pct: 18 },
  ],
  gender: { male: 49.5, female: 50.5 },
  newVoters: {
    label: "Automatic & Undi18 voters added since 2018",
    value: "≈ 750,000",
    note: "Johor's electorate expanded sharply after automatic registration and the lowering of the voting age to 18, reshaping many seats.",
  },
  seatEthnic,
};
